import React, { useState } from "react";
import { signInWithEmailAndPassword, signInWithPopup, sendPasswordResetEmail } from "firebase/auth";
import { auth, db, googleProvider } from "./firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const navigate = useNavigate();

  // Helper function to check if user exists in Firestore by email
  const checkUserExistsByEmail = async (email) => {
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error checking user existence:", error);
      return false;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    try {
      // First check if user exists in Firestore database
      const userExists = await checkUserExistsByEmail(email);

      if (!userExists) {
        setMessage("❌ Account not found. Please sign up first.");
        setIsLoading(false);

        // Redirect to signup after a short delay
        setTimeout(() => {
          navigate("/signup");
        }, 2000);
        return;
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      // Get user's role from Firestore (we know user exists now)
      const userDoc = await getDoc(doc(db, "users", uid));

      if (userDoc.exists()) {
        const role = userDoc.data().role;

        setMessage("✅ Login successful!");

        if (role === "admin") {
          navigate("/admin/upload");
        } else {
          navigate("/dashboard");
        }
      } else {
        // This shouldn't happen since we checked by email, but handle edge case
        setMessage("❌ User record not found. Please contact support.");
      }
    } catch (error) {
      console.error(error);
      let errorMessage = "❌ Login failed. Please check your credentials.";

      if (error.code === "auth/user-not-found") {
        errorMessage = "❌ No account found with this email. Redirecting to signup...";
        setTimeout(() => {
          navigate("/signup");
        }, 2000);
      } else if (error.code === "auth/wrong-password") {
        errorMessage = "❌ Incorrect password.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "❌ Invalid email address.";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "❌ Too many failed attempts. Please try again later.";
      }

      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setMessage("");

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user exists in Firestore by email first
      const userExists = await checkUserExistsByEmail(user.email);

      if (userExists) {
        // User exists, get their role and redirect accordingly
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("email", "==", user.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data();
          const role = userData.role;

          setMessage("✅ Login successful!");

          if (role === "admin") {
            navigate("/admin/upload");
          } else {
            navigate("/dashboard");
          }
        }
      } else {
        // User doesn't exist in our database, redirect to signup
        setMessage("❌ Account not found. Please sign up first. Redirecting...");

        // Sign out the user since they're not in our database
        await auth.signOut();

        setTimeout(() => {
          navigate("/signup");
        }, 2000);
      }
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      let errorMessage = "❌ Google sign-in failed. Please try again.";

      if (error.code === "auth/popup-closed-by-user") {
        errorMessage = "❌ Sign-in cancelled.";
      } else if (error.code === "auth/popup-blocked") {
        errorMessage = "❌ Popup blocked. Please allow popups and try again.";
      }

      setMessage(errorMessage);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!email) {
      setMessage("❌ Please enter your email address first.");
      return;
    }

    // Check if user exists before sending reset email
    const userExists = await checkUserExistsByEmail(email);

    if (!userExists) {
      setMessage("❌ No account found with this email. Please sign up first.");
      setTimeout(() => {
        navigate("/signup");
      }, 2000);
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("✅ Password reset email sent! Check your inbox.");
      setShowForgotPassword(false);
    } catch (error) {
      console.error("Password Reset Error:", error);
      let errorMessage = "❌ Failed to send reset email.";

      if (error.code === "auth/user-not-found") {
        errorMessage = "❌ No account found with this email.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "❌ Invalid email address.";
      }

      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  return (
    <div
      className="min-h-screen bg-white flex items-center justify-center p-6"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}
    >
      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Back Button */}
        <button
          onClick={handleBackToHome}
          className="mb-10 flex items-center text-[13px] font-medium text-gray-600 hover:text-black transition-colors duration-200 group"
        >
          <span className="mr-2 transition-transform duration-200 group-hover:-translate-x-0.5">←</span>
          Back to Home
        </button>

        {/* Login Card */}
        <div className="bg-white border border-black/[0.08] rounded-2xl shadow-sm p-8">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <div className="w-14 h-14 bg-black rounded-2xl flex items-center justify-center">
                <span className="text-white text-xl">🔐</span>
              </div>
            </div>
            <h1 className="text-[28px] font-semibold text-black tracking-[-0.03em] leading-tight mb-2">
              {showForgotPassword ? "Reset Password" : "Welcome Back"}
            </h1>
            <p className="text-[15px] text-gray-500">
              {showForgotPassword
                ? "Enter your email to receive a password reset link"
                : "Sign in to your Residency Companion account"}
            </p>
          </div>

          {!showForgotPassword ? (
            <>
              {/* Google Sign-In Button */}
              <button
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
                className="w-full mb-6 bg-white hover:bg-gray-50 border border-black/[0.15] hover:border-black/30 text-black py-3.5 px-4 rounded-full text-[15px] font-medium transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white flex items-center justify-center"
              >
                {isGoogleLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-black/20 border-t-black mr-2" />
                    Signing in with Google...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="mb-6 flex items-center">
                <div className="flex-1 border-t border-black/[0.06]" />
                <span className="px-4 text-[13px] text-gray-400">or</span>
                <div className="flex-1 border-t border-black/[0.06]" />
              </div>

              {/* Email/Password Form */}
              <form onSubmit={handleLogin} className="space-y-5">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-[13px] font-medium text-black mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white border border-black/[0.1] rounded-xl focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200 text-black placeholder-gray-400 text-[15px]"
                  />
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-[13px] font-medium text-black mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-4 py-3 bg-white border border-black/[0.1] rounded-xl focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200 text-black placeholder-gray-400 text-[15px] pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
                    >
                      {showPassword ? "👁️" : "🙈"}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white py-3.5 px-4 rounded-full text-[15px] font-medium transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white mr-2" />
                      Signing In...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>

              {/* Forgot Password Link */}
              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-[13px] text-gray-500 hover:text-black transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            </>
          ) : (
            /* Forgot Password Form */
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <label htmlFor="reset-email" className="block text-[13px] font-medium text-black mb-1.5">
                  Email Address
                </label>
                <input
                  id="reset-email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white border border-black/[0.1] rounded-xl focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200 text-black placeholder-gray-400 text-[15px]"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white py-3.5 px-4 rounded-full text-[15px] font-medium transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white mr-2" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Email"
                )}
              </button>

              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="w-full text-[13px] text-gray-500 hover:text-black py-2 transition-colors"
              >
                ← Back to Sign In
              </button>
            </form>
          )}

          {/* Message */}
          {message && (
            <div
              className={`mt-6 p-4 rounded-xl text-[14px] font-medium ${
                message.includes("✅")
                  ? "bg-black/[0.04] text-gray-800 border border-black/[0.08]"
                  : "bg-red-50/90 text-red-900 border border-red-200/80"
              }`}
            >
              {message}
            </div>
          )}

          {!showForgotPassword && (
            <>
              {/* Divider */}
              <div className="my-8 flex items-center">
                <div className="flex-1 border-t border-black/[0.06]" />
                <span className="px-4 text-[13px] text-gray-400">or</span>
                <div className="flex-1 border-t border-black/[0.06]" />
              </div>

              {/* Sign Up Link */}
              <div className="text-center">
                <p className="text-[15px] text-gray-500 mb-4">Don't have an account?</p>
                <button
                  type="button"
                  onClick={() => navigate("/signup")}
                  className="w-full border border-black/[0.15] hover:border-black/30 text-black hover:bg-gray-50 py-3.5 px-4 rounded-full text-[15px] font-medium transition-all duration-200"
                >
                  Create Account
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-[12px] text-gray-400">
            By signing in, you agree to our{" "}
            <span className="text-gray-500 cursor-not-allowed opacity-70">Terms of Service</span>
            {" "}and{" "}
            <span className="text-gray-500 cursor-not-allowed opacity-70">Privacy Policy</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
